import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowRight, User, Phone, Mail, GraduationCap } from "lucide-react";
import { useCourses } from "@/hooks/useCourses";
import { useToast } from "@/hooks/use-toast";

interface FormData {
  studentFirstName: string;
  studentFirstSurname: string;
  studentSecondSurname?: string;
  studentClass: string;
  parentEmail: string;
  parentPhone: string;
  selectedActivities: string[];
}

interface StudentDataStepProps {
  form: UseFormReturn<FormData>;
  onNext: (data: FormData) => void;
  isLoading?: boolean;
}

export function StudentDataStep({ form, onNext, isLoading = false }: StudentDataStepProps) {
  const { courses, loading: coursesLoading } = useCourses();
  const { toast } = useToast();

  const handleSubmit = (data: FormData) => {
    onNext(data);
  };

  const handleError = () => {
    toast({
      title: "Errores en el formulario",
      description: "Por favor, revisa los campos marcados en rojo",
      variant: "destructive",
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
          <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Información del Alumno/a</h2>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
          Completa los datos del Alumno/a para comenzar el proceso de inscripción
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, handleError)} className="space-y-6 sm:space-y-8">
          {/* Datos del estudiante */}
          <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50/50">
            <CardHeader className="pb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-xl text-gray-900">Datos Personales</CardTitle>
                  <CardDescription className="text-gray-600">
                    Información básica del Alumno/a
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="studentFirstName"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        Nombre *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ej: María" 
                          className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="studentFirstSurname"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-gray-700">
                        Primer apellido *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ej: García" 
                          className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="studentSecondSurname"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-gray-700">
                        Segundo apellido
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Ej: López" 
                          className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="studentClass"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" />
                      Curso *
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300">
                          <SelectValue placeholder="Selecciona el curso del Alumno/a" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {coursesLoading ? (
                          <SelectItem value="loading" disabled>
                            <div className="flex items-center space-x-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Cargando clases...</span>
                            </div>
                          </SelectItem>
                        ) : (
                          courses.map((course) => (
                            <SelectItem key={course.value} value={course.value}>
                              {course.label}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Datos de contacto */}
          <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-gray-50/50">
            <CardHeader className="pb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-xl text-gray-900">Datos de Contacto</CardTitle>
                  <CardDescription className="text-gray-600">
                    Información de contacto del padre/madre o tutor
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <FormField
                  control={form.control}
                  name="parentEmail"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email del padre/madre *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="correo@ejemplo.com" 
                          className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="parentPhone"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Teléfono de contacto *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="123 456 789" 
                          className="h-11 sm:h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all duration-200 hover:border-gray-300"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Información adicional */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-4 sm:p-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-blue-900">Información importante</h4>
                <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                  <li>• Todos los campos marcados con * son obligatorios.</li>
                  <li>• Si tienes alguna duda puedes ponerte en contacto con el centro en horario laboral.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Botón de continuar */}
          <div className="flex justify-center pt-4">
            <Button 
              type="submit" 
              disabled={isLoading}
              className="h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                  Validando datos...
                </>
              ) : (
                <>
                  Continuar a actividades
                  <ArrowRight className="ml-3 h-5 w-5" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}